import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Fade
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { modernTheme, iconBoxSx, monthLabel } from './theme';
import { getLeaderTeamGMV } from '../../services/commissionService';

const fmt = (v) => new Intl.NumberFormat('vi-VN').format(v || 0) + 'đ';

const LeaderTeamWidget = ({ leaderData, userSummary, isMobile }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);

  const team = leaderData?.team;
  const minHours = 90;
  const liveHours = userSummary?.currentPeriod?.hours || 0;
  const hoursProgress = Math.min((liveHours / minHours) * 100, 100);

  useEffect(() => {
    const fetchTeamGMV = async () => {
      setLoadingMembers(true);
      try {
        const result = await getLeaderTeamGMV();
        if (result.success) {
          setTeamMembers(result.data.members || []);
        }
      } catch (error) {
        console.error('Error fetching team GMV:', error);
      } finally {
        setLoadingMembers(false);
      }
    };
    fetchTeamGMV();
  }, []);

  if (!team) return null;

  // Từ 10/2026 thưởng tăng trưởng so với KỶ LỤC 6 tháng (GMV đã chốt) thay vì tháng trước
  const isPeak = team.growthBaseline === 'peak';
  const baseGMV = isPeak ? (team.baselineTeamGMV || 0) : team.prevTeamTotalGMV;
  const growthPercent = baseGMV > 0
    ? Math.round((team.teamTotalGMV - baseGMV) / baseGMV * 100)
    : 0;
  const isGrowth = isPeak ? team.teamTotalGMV > baseGMV : team.teamTotalGMV >= baseGMV;
  const baseLabel = isPeak ? `vs kỷ lục ${monthLabel(team.baselineTeamMonth)}` : 'vs tháng trước';
  const tiktokPassed = isPeak
    ? team.tiktokCurrentGMV > (team.baselineTiktokGMV || 0)
    : team.tiktokGrowthPercent >= team.tiktokGrowthThreshold;

  return (
    <Fade in timeout={800}>
      <Card sx={{
        borderRadius: 3,
        border: '1px solid #e9d5ff',
        boxShadow: modernTheme.shadows.md,
        background: 'linear-gradient(135deg, #faf5ff 0%, #f5f3ff 50%, #ede9fe 100%)',
      }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
            <Box sx={iconBoxSx('linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)', 44)}>
              <GroupsIcon sx={{ color: 'white', fontSize: 22 }} />
            </Box>
            <Box sx={{ ml: 1.5 }}>
              <Typography variant="subtitle1" sx={{ color: '#581c87', fontWeight: 700, lineHeight: 1.2 }}>
                Tổng quan Team
              </Typography>
              <Typography variant="caption" sx={{ color: '#7c3aed', fontSize: '0.7rem' }}>
                Lead Team Live
              </Typography>
            </Box>
          </Box>

          {/* Summary Cards */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
            {/* Team GMV */}
            <Box sx={{
              flex: 1, minWidth: 140, p: 1.5, borderRadius: 2,
              bgcolor: 'white', border: '1px solid #e9d5ff',
            }}>
              <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }}>
                Team GMV tháng này
              </Typography>
              <Typography variant="h6" sx={{ color: '#581c87', fontWeight: 800, mt: 0.5, fontSize: '1.1rem' }}>
                {fmt(team.teamTotalGMV)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                {isGrowth ? (
                  <TrendingUpIcon sx={{ fontSize: 14, color: '#059669', mr: 0.3 }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: 14, color: '#dc2626', mr: 0.3 }} />
                )}
                <Typography variant="caption" sx={{
                  color: isGrowth ? '#059669' : '#dc2626', fontSize: '0.68rem', fontWeight: 600,
                }}>
                  {growthPercent > 0 ? '+' : ''}{growthPercent}% {baseLabel}
                </Typography>
              </Box>
            </Box>

            {/* TikTok GMV */}
            <Box sx={{
              flex: 1, minWidth: 140, p: 1.5, borderRadius: 2,
              bgcolor: 'white', border: '1px solid #e9d5ff',
            }}>
              <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }}>
                TikTok GMV
              </Typography>
              <Typography variant="h6" sx={{ color: '#581c87', fontWeight: 800, mt: 0.5, fontSize: '1.1rem' }}>
                {fmt(team.tiktokCurrentGMV)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <Chip
                  label={`${team.tiktokGrowthPercent >= 0 ? '+' : ''}${team.tiktokGrowthPercent}%`}
                  size="small"
                  sx={{
                    height: 18, fontSize: '0.6rem', fontWeight: 700,
                    bgcolor: tiktokPassed ? '#dcfce7' : '#fef3c7',
                    color: tiktokPassed ? '#059669' : '#d97706',
                  }}
                />
                <Typography variant="caption" sx={{ color: modernTheme.colors.gray[500], fontSize: '0.6rem', ml: 0.5 }}>
                  {isPeak ? `vs kỷ lục ${monthLabel(team.baselineTiktokMonth)}` : `cần ${team.tiktokGrowthThreshold}%+`}
                </Typography>
              </Box>
            </Box>

            {/* KPI Hours */}
            <Box sx={{
              flex: 1, minWidth: 140, p: 1.5, borderRadius: 2,
              bgcolor: 'white', border: '1px solid #e9d5ff',
            }}>
              <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }}>
                Giờ live cá nhân
              </Typography>
              <Typography variant="h6" sx={{ color: '#581c87', fontWeight: 800, mt: 0.5, fontSize: '1.1rem' }}>
                {Math.round(liveHours * 10) / 10}/{minHours}h
              </Typography>
              <LinearProgress
                variant="determinate"
                value={hoursProgress}
                sx={{
                  mt: 0.5, height: 6, borderRadius: 3,
                  bgcolor: '#e9d5ff',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: hoursProgress >= 100 ? '#059669' : '#7c3aed',
                    borderRadius: 3,
                  }
                }}
              />
            </Box>
          </Box>

          {/* Team Members GMV Table */}
          {!loadingMembers && teamMembers.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{
                color: '#7c3aed', fontWeight: 700, fontSize: '0.65rem',
                textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 1,
              }}>
                GMV theo thành viên
              </Typography>
              <TableContainer sx={{
                borderRadius: 2, border: '1px solid #e9d5ff',
                bgcolor: 'white',
              }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#faf5ff' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#581c87', py: 1 }}>
                        Thành viên
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#581c87', py: 1 }}>
                        Số phiên
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#581c87', py: 1 }}>
                        GMV
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamMembers.map((member) => (
                      <TableRow key={member.userId} hover>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.75 }}>
                          {member.fullName}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.75, color: modernTheme.colors.gray[600] }}>
                          {member.revenueCount}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.75, fontWeight: 700, color: '#581c87' }}>
                          {fmt(member.totalGMV)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </CardContent>
      </Card>
    </Fade>
  );
};

export default LeaderTeamWidget;
